/**
 * Notifications Settings Page
 */

import { useEffect, useState } from 'react';
import { Logger, LogCategory } from '@/utils/logger';
import { AppLayout, PageHeader } from '@/components/layout';
import { Card, CardHeader, CardContent } from '@/components/ui/Card';
import { Select } from '@/components/ui/Select';
import { Button } from '@/components/ui/Button';
import { useToast } from '@/components/ui/Toast';
import { useCompany, useAuthStore } from '@/stores/authStore';
import { supabase } from '@/lib/supabase';
import { Bell, Mail, AlertTriangle, CreditCard, FileText, Shield } from 'lucide-react';

interface ToggleSwitchProps {
  enabled: boolean;
  onChange: (enabled: boolean) => void;
  disabled?: boolean;
}

function ToggleSwitch({ enabled, onChange, disabled }: ToggleSwitchProps) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={enabled}
      disabled={disabled}
      onClick={() => onChange(!enabled)}
      className={`
        relative inline-flex items-center h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent
        transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2
        ${enabled ? 'bg-primary-600' : 'bg-slate-200'}
        ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
      `}
    >
      <span
        className={`
          pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0
          transition duration-200 ease-in-out
          ${enabled ? 'translate-x-5' : 'translate-x-0'}
        `}
      />
    </button>
  );
}

export function NotificationsSettingsPage() {
  const company = useCompany();
  const updateCompany = useAuthStore((state) => state.updateCompany);
  const { success: showSuccess, error: showError } = useToast();

  const [settings, setSettings] = useState({
    frequency: 'daily',
    emailEnabled: true,
    channels: {
      billing: true,
      phoneFlagged: true,
      creditsLow: true,
      weeklyReport: true,
      securityAlerts: true,
    },
  });
  const [isDirty, setIsDirty] = useState(false);

  useEffect(() => {
    Logger.info(LogCategory.UI, 'NotificationsSettingsPage mounted', 'NotificationsSettingsPage');

    if (company?.settings) {
      setSettings({
        frequency: company.settings.notificationFrequency,
        emailEnabled: company.settings.emailNotifications,
        channels: {
          billing: company.settings.notifyOnBillingChanges ?? false,
          phoneFlagged: company.settings.notifyOnPhoneFlagged ?? false,
          creditsLow: company.settings.notifyOnCreditsLow ?? false,
          weeklyReport: true,
          securityAlerts: true,
        },
      });
    }
  }, [company]);

  const handleFrequencyChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    Logger.debug(LogCategory.UI, 'Notification frequency changed', 'NotificationsSettingsPage');
    setSettings((prev) => ({ ...prev, frequency: e.target.value }));
    setIsDirty(true);
  };

  const handleToggle = (key: keyof typeof settings.channels) => {
    Logger.debug(LogCategory.UI, `Toggle ${key}`, 'NotificationsSettingsPage');
    setSettings((prev) => ({
      ...prev,
      channels: { ...prev.channels, [key]: !prev.channels[key] },
    }));
    setIsDirty(true);
  };

  const handleEmailToggle = () => {
    Logger.debug(LogCategory.UI, 'Email notifications toggled', 'NotificationsSettingsPage');
    setSettings((prev) => ({ ...prev, emailEnabled: !prev.emailEnabled }));
    setIsDirty(true);
  };

  const handleSave = async () => {
    Logger.info(LogCategory.DATA, 'Saving notification settings', 'NotificationsSettingsPage');

    try {
      // Persist to Supabase
      const { error } = await (supabase
        .from('companies') as any)
        .update({
          notification_frequency: settings.frequency,
          email_notifications: settings.emailEnabled,
          notify_on_billing_changes: settings.channels.billing,
          notify_on_phone_flagged: settings.channels.phoneFlagged,
          notify_on_credits_low: settings.channels.creditsLow,
        })
        .eq('id', company!.id);

      if (error) throw error;

      // Update local state
      updateCompany({
        settings: {
          ...company!.settings,
          notificationFrequency: settings.frequency as 'hourly' | 'daily' | 'weekly',
          emailNotifications: settings.emailEnabled,
          notifyOnBillingChanges: settings.channels.billing,
          notifyOnPhoneFlagged: settings.channels.phoneFlagged,
          notifyOnCreditsLow: settings.channels.creditsLow,
        },
      });
      setIsDirty(false);
      showSuccess('Settings saved', 'Your notification preferences have been updated.');
    } catch (err) {
      Logger.error(LogCategory.DATA, 'Failed to save notification settings', 'NotificationsSettingsPage', undefined, err);
      showError('Save failed', 'Failed to save notification settings. Please try again.');
    }
  };

  const notificationChannels = [
    { key: 'phoneFlagged', label: 'Phone Number Flagged', description: 'Get notified when a phone number is flagged by a reputation source', icon: AlertTriangle },
    { key: 'creditsLow', label: 'Credits Running Low', description: 'Get notified when your credit balance falls below the threshold', icon: CreditCard },
    { key: 'billing', label: 'Billing Updates', description: 'Receive invoices and payment confirmations', icon: FileText },
    { key: 'weeklyReport', label: 'Weekly Summary', description: 'Receive a weekly summary of your reputation data', icon: FileText },
    { key: 'securityAlerts', label: 'Security Alerts', description: 'Get notified about security-related events', icon: Shield },
  ];

  return (
    <AppLayout>
      <PageHeader
        title="Notification Settings"
        description="Configure how and when you receive notifications"
      />

      <div className="max-w-3xl space-y-6">
        {/* Email Notifications */}
        <Card>
          <CardHeader
            title="Email Notifications"
            description="Receive notifications via email"
          />
          <CardContent>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 bg-primary-100 rounded-lg flex items-center justify-center">
                  <Mail className="h-5 w-5 text-primary-600" />
                </div>
                <div>
                  <p className="font-medium text-slate-900">Email Notifications</p>
                  <p className="text-sm text-slate-500">
                    {settings.emailEnabled ? 'Enabled' : 'Disabled'}
                  </p>
                </div>
              </div>
              <ToggleSwitch enabled={settings.emailEnabled} onChange={handleEmailToggle} />
            </div>

            {settings.emailEnabled && (
              <div className="mt-6">
                <Select
                  label="Notification Frequency"
                  value={settings.frequency}
                  onChange={handleFrequencyChange}
                  options={[
                    { value: 'hourly', label: 'Hourly digest' },
                    { value: 'daily', label: 'Daily digest' },
                    { value: 'weekly', label: 'Weekly digest' },
                  ]}
                  hint="How often you receive notification digests"
                />
              </div>
            )}
          </CardContent>
        </Card>

        {/* Notification Channels */}
        <Card>
          <CardHeader
            title="Notification Channels"
            description="Choose what events to be notified about"
          />
          <CardContent>
            <div className="space-y-4">
              {notificationChannels.map((channel) => (
                <div
                  key={channel.key}
                  className="flex items-center justify-between p-4 border border-slate-200 rounded-lg"
                >
                  <div className="flex items-start gap-3">
                    <div className="h-9 w-9 bg-slate-100 rounded-lg flex items-center justify-center mt-0.5">
                      <channel.icon className="h-5 w-5 text-slate-600" />
                    </div>
                    <div>
                      <p className="font-medium text-slate-900">{channel.label}</p>
                      <p className="text-sm text-slate-500">{channel.description}</p>
                    </div>
                  </div>
                  <ToggleSwitch
                    enabled={settings.channels[channel.key as keyof typeof settings.channels]}
                    onChange={() => handleToggle(channel.key as keyof typeof settings.channels)}
                    disabled={!settings.emailEnabled}
                  />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Save Button */}
        {isDirty && (
          <div className="flex justify-end">
            <Button onClick={handleSave}>
              Save Changes
            </Button>
          </div>
        )}
      </div>
    </AppLayout>
  );
}
